(() => {
  if (customElements.get('tiffany-import-consent')) return;
  const storageKey = 'tiffany-import-consent-v3';
  const euCountries = new Set('AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE'.split(','));
  const storage = {
    get() { try { return JSON.parse(sessionStorage.getItem(storageKey)); } catch { return null; } },
    set(value) { try { value ? sessionStorage.setItem(storageKey, JSON.stringify(value)) : sessionStorage.removeItem(storageKey); } catch {} }
  };
  let accepted = document.currentScript?.dataset.cartCount === '0' ? null : storage.get();
  let country = null;
  let saving = false;
  let requestVersion = 0;
  const destinations = () => document.querySelectorAll('[data-tm-destination]');
  const consents = () => document.querySelectorAll('tiffany-import-consent');
  const importRequired = () => Boolean(country && !euCountries.has(country));
  const acceptanceValid = version => Boolean(accepted && accepted.country === country && accepted.version === version && Date.now() - Date.parse(accepted.time) < 7200000);
  const clear = () => { accepted = null; storage.set(null); };

  function synchronize() {
    for (const destination of destinations()) destination.update?.();
    for (const consent of consents()) consent.update?.();
  }

  function attributes() {
    const consent = document.querySelector('tiffany-import-consent');
    const version = consent?.dataset.version || '2026-10-25-v3';
    const valid = importRequired() && acceptanceValid(version);
    return {
      'Delivery country selected in cart': country || '',
      'Import charges acknowledged': importRequired() ? (valid ? 'Yes' : '') : 'Not requested - EU destination selected',
      'Import notice text': importRequired() ? consent?.dataset.notice || '' : '',
      'Import acknowledgment time': valid ? accepted.time : '',
      'Import notice version': version,
      'Import notice language': consent?.dataset.language || document.documentElement.lang
    };
  }

  async function cartRequest(path, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const root = new URL(window.Shopify?.routes?.root || '/', location.origin);
      const response = await fetch(new URL(path, root), {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, ...options
      });
      if (!response.ok) throw new Error('destination-request-failed');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }

  async function changeDestination(select) {
    const destination = select.closest('[data-tm-destination]');
    const next = select.value;
    if (!destination || saving || !next || next === country) return;
    if (!Array.from(select.options).some(option => option.value === next && !option.disabled)) return;
    const previous = { country, accepted };
    const revision = ++requestVersion;
    country = next;
    clear();
    saving = true;
    for (const element of destinations()) element.hideError?.();
    synchronize();
    try {
      const pending = customElements.get('cart-items-component')?.pendingQuantityUpdates || [];
      await Promise.allSettled(pending);
      const cart = await cartRequest('cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: attributes() }) });
      if (cart.attributes?.['Delivery country selected in cart'] !== country) throw new Error('destination-not-saved');
    } catch {
      country = previous.country;
      accepted = previous.accepted;
      storage.set(accepted);
      for (const element of destinations()) element.showError?.();
    } finally {
      if (revision === requestVersion) {
        saving = false;
        synchronize();
      }
    }
  }

  class TiffanyCartDestination extends HTMLElement {
    connectedCallback() {
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this, { childList: true, subtree: true });
      this.update();
      for (const consent of consents()) consent.update?.();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    update() {
      this.select = this.querySelector('[data-tm-country]');
      if (!this.select) return;
      if (country === null) country = this.select.value || '';
      this.select.value = country || '';
      this.select.disabled = saving;
      this.dataset.country = country || '';
      this.toggleAttribute('aria-busy', saving);
      const status = this.querySelector('[data-tm-destination-status]');
      if (status) status.hidden = !saving;
      const hint = this.querySelector('[data-tm-unsupported]');
      if (hint) hint.hidden = Boolean(country);
    }
    hideError() {
      const error = this.querySelector('[data-tm-destination-error]');
      if (error) error.hidden = true;
      this.select?.removeAttribute('aria-invalid');
    }
    showError() {
      const error = this.querySelector('[data-tm-destination-error]');
      if (error) { error.textContent = this.dataset.error; error.hidden = false; }
    }
    validate() {
      if (saving) return false;
      if (country && this.select?.value === country) return true;
      this.select?.setAttribute('aria-invalid', 'true');
      this.select?.focus({ preventScroll: true });
      this.select?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      return false;
    }
  }

  class TiffanyImportConsent extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-tm-import-check]')) return;
        if (event.target.checked && importRequired() && !saving) {
          accepted = { country, version: this.dataset.version, time: new Date().toISOString() };
          storage.set(accepted);
        } else clear();
        for (const consent of consents()) { consent.hideError(); consent.update(); }
      }, options);
      this.addEventListener('invalid', event => {
        if (event.target.matches('[data-tm-import-check]')) { event.preventDefault(); this.showError(); }
      }, { ...options, capture: true });
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this, { childList: true, subtree: true });
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
    update() {
      if (country === null) return;
      this.dataset.country = country;
      this.input = this.querySelector('[data-tm-import-check]');
      const required = importRequired();
      const notice = this.querySelector('[data-tm-import-notice]');
      if (notice) notice.hidden = !required;
      if (this.input) {
        this.input.checked = required && acceptanceValid(this.dataset.version);
        this.input.required = required;
        this.input.disabled = !required || saving;
      }
      const values = attributes();
      for (const field of this.querySelectorAll('[data-tm-consent-attribute]')) field.value = values[field.dataset.tmConsentAttribute] || '';
      for (const button of this.closest('.cart__ctas')?.querySelectorAll('button[name="checkout"]') || []) {
        button.disabled = saving || !country || (required && !this.input?.checked) || this.dataset.cartCount === '0';
      }
      if (!required) this.hideError();
    }
    hideError() {
      const error = this.querySelector('.tm-import-consent__error');
      if (error) error.hidden = true;
      this.input?.removeAttribute('aria-invalid');
    }
    showError() {
      const error = this.querySelector('.tm-import-consent__error');
      if (error) error.hidden = false;
      this.input?.setAttribute('aria-invalid', 'true');
      this.input?.focus({ preventScroll: true });
      this.input?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
    validate() {
      this.update();
      if (!this.querySelector('[data-tm-destination]')?.validate()) return false;
      if (!importRequired() || this.input?.checked) return true;
      this.showError();
      return false;
    }
  }

  customElements.define('tiffany-cart-destination', TiffanyCartDestination);
  customElements.define('tiffany-import-consent', TiffanyImportConsent);
  document.addEventListener('change', event => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-tm-country]')) return;
    event.stopImmediatePropagation();
    void changeDestination(select);
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="checkout"]');
    if (!button) return;
    const consent = button.closest('.cart__ctas')?.querySelector('tiffany-import-consent');
    if (saving || (consent && !consent.validate())) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', event => {
    if (!event.target.matches('form.cart-form')) return;
    if (saving) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    for (const consent of consents()) {
      if (!consent.validate()) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    }
  }, true);
  window.addEventListener('pageshow', async event => {
    if (!event.persisted || saving) return;
    const revision = requestVersion;
    try {
      const cart = await cartRequest('cart.js');
      if (revision !== requestVersion || saving) return;
      const saved = cart.attributes?.['Delivery country selected in cart'];
      if (saved && saved !== country) {
        const select = document.querySelector('[data-tm-country]');
        country = Array.from(select?.options || []).some(option => option.value === saved && !option.disabled) ? saved : '';
        clear();
      }
      if (!cart.item_count) clear();
      synchronize();
    } catch { synchronize(); }
  });
})();
